/**
 * AR Grid Display V3 - JavaScript Module
 * Handles: Image Sliders, Countdown Timers, Video Embeds, Entrance Animations
 */

class ARGridV3 {
  constructor() {
    this.sliders = [];
    this.countdowns = [];
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.initComponents());
    } else {
      this.initComponents();
    }
  }

  initComponents() {
    this.initSliders();
    this.initCountdowns();
    this.initVideos();
    this.initAnimations();
    this.initProductSlides();
  }

  // ============================================================================
  // IMAGE SLIDER
  // ============================================================================
  initSliders() {
    const sliderElements = document.querySelectorAll('[data-slider]');

    sliderElements.forEach(sliderEl => {
      const sliderId = sliderEl.getAttribute('data-slider');
      const configEl = document.querySelector(`[data-slider-config="${sliderId}"]`);

      if (!configEl) return;

      let config = { autoplay: false, speed: 5000 };
      try {
        config = JSON.parse(configEl.textContent);
      } catch (e) {
        console.warn('Failed to parse slider config:', e);
      }

      const slider = new ARSlider(sliderEl, config);
      this.sliders.push(slider);
    });
  }

  // ============================================================================
  // COUNTDOWN TIMER
  // ============================================================================
  initCountdowns() {
    const countdownElements = document.querySelectorAll('[data-countdown]');

    countdownElements.forEach(countdownEl => {
      const targetDate = countdownEl.getAttribute('data-countdown');
      if (!targetDate) return;

      const countdown = new ARCountdown(countdownEl, targetDate);
      this.countdowns.push(countdown);
    });
  }

  // ============================================================================
  // VIDEO EMBED
  // ============================================================================
  initVideos() {
    const videoContainers = document.querySelectorAll('[data-video-container]');

    videoContainers.forEach(container => {
      const embedEl = container.querySelector('[data-video-url]');
      if (!embedEl) return;

      const videoUrl = embedEl.getAttribute('data-video-url');
      if (!videoUrl) return;

      const iframe = this.createVideoEmbed(videoUrl);
      if (iframe) {
        embedEl.appendChild(iframe);

        // Hide poster after video loads
        const poster = container.querySelector('.ar-grid-item-v3__video-poster');
        if (poster) {
          iframe.addEventListener('load', () => {
            poster.style.opacity = '0';
            setTimeout(() => poster.style.display = 'none', 300);
          });
        }
      }
    });
  }

  createVideoEmbed(url) {
    const iframe = document.createElement('iframe');
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute('allowfullscreen', '');
    iframe.setAttribute('allow', 'autoplay; encrypted-media');

    // YouTube
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      const videoId = this.extractYouTubeId(url);
      if (videoId) {
        iframe.src = `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&controls=0&showinfo=0&rel=0&modestbranding=1`;
        return iframe;
      }
    }

    // Vimeo
    if (url.includes('vimeo.com')) {
      const videoId = this.extractVimeoId(url);
      if (videoId) {
        iframe.src = `https://player.vimeo.com/video/${videoId}?autoplay=1&muted=1&loop=1&background=1&controls=0`;
        return iframe;
      }
    }

    console.warn('Unsupported video URL:', url);
    return null;
  }

  extractYouTubeId(url) {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/,
      /youtube\.com\/embed\/([^&\n?#]+)/
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) return match[1];
    }

    return null;
  }

  extractVimeoId(url) {
    const pattern = /vimeo\.com\/(?:video\/)?(\d+)/;
    const match = url.match(pattern);
    return match && match[1] ? match[1] : null;
  }

  // ============================================================================
  // ENTRANCE ANIMATIONS
  // ============================================================================
  initAnimations() {
    // Check for reduced motion preference
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    const animatedElements = document.querySelectorAll('[data-grid-item-v3]');

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.style.opacity = '1';
            observer.unobserve(entry.target);
          }
        });
      }, {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      });

      animatedElements.forEach(el => {
        // Only animate elements with animation classes
        if (el.classList.contains('animate--fade-in') ||
            el.classList.contains('animate--slide-up') ||
            el.classList.contains('animate--slide-left') ||
            el.classList.contains('animate--scale')) {
          el.style.opacity = '0';
          observer.observe(el);
        }
      });
    } else {
      // Fallback: show all elements immediately
      animatedElements.forEach(el => {
        el.style.opacity = '1';
      });
    }
  }

  // ============================================================================
  // PRODUCT SLIDER - ADD TO CART
  // ============================================================================
  initProductSlides() {
    const productButtons = document.querySelectorAll('button[data-add-products]');

    console.log('[AR Grid V3] Found', productButtons.length, 'add-to-cart buttons');

    productButtons.forEach((button, index) => {
      const productsData = button.getAttribute('data-products');
      console.log('[AR Grid V3] Button', index, 'products data:', productsData);

      button.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        console.log('[AR Grid V3] Button clicked');

        const productsData = button.getAttribute('data-products');
        if (!productsData) {
          console.warn('[AR Grid V3] No product data found');
          return;
        }

        try {
          const products = JSON.parse(productsData);
          console.log('[AR Grid V3] Parsed products:', products);
          this.addProductsToCart(products, button);
        } catch (error) {
          console.error('[AR Grid V3] Failed to parse product data:', error, productsData);
        }
      });
    });
  }

  async addProductsToCart(products, buttonElement) {
    // Filter out any empty/null products
    const validProducts = products.filter(p => p && p.id);

    if (validProducts.length === 0) {
      console.warn('No valid products to add');
      return;
    }

    // Show loading state
    const originalText = buttonElement.innerHTML;
    buttonElement.disabled = true;
    buttonElement.innerHTML = '<svg class="icon icon-cart" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 16C4.9 16 4.01 16.9 4.01 18C4.01 19.1 4.9 20 6 20C7.1 20 8 19.1 8 18C8 16.9 7.1 16 6 16ZM0 0V2H2L5.6 9.59L4.25 12.04C4.09 12.32 4 12.65 4 13C4 14.1 4.9 15 6 15H18V13H6.42C6.28 13 6.17 12.89 6.17 12.75L6.2 12.63L7.1 11H14.55C15.3 11 15.96 10.59 16.3 9.97L19.88 3.48C19.96 3.34 20 3.17 20 3C20 2.45 19.55 2 19 2H4.21L3.27 0H0ZM16 16C14.9 16 14.01 16.9 14.01 18C14.01 19.1 14.9 20 16 20C17.1 20 18 19.1 18 18C18 16.9 17.1 16 16 16Z" fill="currentColor"/></svg> Accepting...';

    try {
      // Use Shopify's cart API to add multiple items
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: validProducts
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add products to cart');
      }

      const result = await response.json();

      // Success feedback
      buttonElement.innerHTML = '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7 13L3 9L1.5 10.5L7 16L19 4L17.5 2.5L7 13Z" fill="currentColor"/></svg> Challenge Accepted!';
      buttonElement.style.backgroundColor = '#10b981';
      buttonElement.style.borderColor = '#ffffff';

      // Trigger cart drawer/update if theme has it
      if (typeof window.theme !== 'undefined' && window.theme.cart) {
        window.theme.cart.refresh();
      }

      // Dispatch custom event for cart updates
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { items: validProducts, response: result }
      }));

      // Publish to theme's pub/sub system for cart drawer and abandoned cart recovery
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ar-grid-v3', cartData: result });
      }

      // Reset button after 2 seconds
      setTimeout(() => {
        buttonElement.innerHTML = originalText;
        buttonElement.style.backgroundColor = '';
        buttonElement.style.borderColor = '';
        buttonElement.disabled = false;
      }, 2000);

    } catch (error) {
      console.error('Error adding products to cart:', error);

      // Error feedback
      buttonElement.innerHTML = '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM11 15H9V13H11V15ZM11 11H9V5H11V11Z" fill="currentColor"/></svg> Error';
      buttonElement.style.backgroundColor = '#ef4444';
      buttonElement.style.borderColor = '#ffffff';

      // Reset button after 2 seconds
      setTimeout(() => {
        buttonElement.innerHTML = originalText;
        buttonElement.style.backgroundColor = '';
        buttonElement.style.borderColor = '';
        buttonElement.disabled = false;
      }, 2000);
    }
  }

}

// ============================================================================
// SLIDER CLASS
// ============================================================================
class ARSlider {
  constructor(element, config) {
    this.element = element;
    this.config = config;
    this.track = element.querySelector('.ar-grid-item-v3__slider-track');
    this.slides = Array.from(element.querySelectorAll('.ar-grid-item-v3__slider-slide'));
    this.dots = Array.from(element.querySelectorAll('.ar-grid-item-v3__slider-dot'));
    this.currentSlide = 0;
    this.autoplayInterval = null;

    if (this.slides.length === 0) return;

    this.init();
  }

  init() {
    // Bind dot click handlers
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        this.goToSlide(index);
        this.stopAutoplay();
      });
    });

    // Pause autoplay on hover
    this.element.addEventListener('mouseenter', () => this.stopAutoplay());
    this.element.addEventListener('mouseleave', () => this.startAutoplay());

    // Touch/swipe support
    this.initTouchSupport();

    // Start autoplay if enabled
    if (this.config.autoplay) {
      this.startAutoplay();
    }

    // Show first slide
    this.goToSlide(0);
  }

  goToSlide(index) {
    if (index < 0) index = this.slides.length - 1;
    if (index >= this.slides.length) index = 0;

    this.currentSlide = index;

    // Update track position
    this.track.style.transform = `translateX(-${index * 100}%)`;

    // Update dots
    this.dots.forEach((dot, i) => {
      dot.classList.toggle('active', i === index);
    });
  }

  nextSlide() {
    this.goToSlide(this.currentSlide + 1);
  }

  prevSlide() {
    this.goToSlide(this.currentSlide - 1);
  }

  startAutoplay() {
    if (!this.config.autoplay || this.slides.length <= 1) return;

    this.stopAutoplay();
    this.autoplayInterval = setInterval(() => {
      this.nextSlide();
    }, this.config.speed);
  }

  stopAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }

  initTouchSupport() {
    let touchStartX = 0;
    let touchEndX = 0;

    this.element.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });

    this.element.addEventListener('touchend', (e) => {
      touchEndX = e.changedTouches[0].screenX;
      this.handleSwipe(touchStartX, touchEndX);
    }, { passive: true });
  }

  handleSwipe(startX, endX) {
    const swipeThreshold = 50;
    const diff = startX - endX;

    if (Math.abs(diff) < swipeThreshold) return;

    if (diff > 0) {
      // Swipe left - next slide
      this.nextSlide();
    } else {
      // Swipe right - previous slide
      this.prevSlide();
    }

    this.stopAutoplay();
  }
}

// ============================================================================
// COUNTDOWN CLASS
// ============================================================================
class ARCountdown {
  constructor(element, targetDate) {
    this.element = element;
    this.targetDate = new Date(targetDate);
    this.daysEl = element.querySelector('[data-days]');
    this.hoursEl = element.querySelector('[data-hours]');
    this.minutesEl = element.querySelector('[data-minutes]');
    this.secondsEl = element.querySelector('[data-seconds]');
    this.interval = null;

    if (isNaN(this.targetDate.getTime())) {
      console.warn('Invalid countdown date:', targetDate);
      return;
    }

    this.start();
  }

  start() {
    // Update immediately
    this.update();

    // Update every second
    this.interval = setInterval(() => {
      this.update();
    }, 1000);
  }

  update() {
    const now = new Date();
    const distance = this.targetDate - now;

    if (distance < 0) {
      this.stop();
      this.showExpired();
      return;
    }

    const days = Math.floor(distance / (1000 * 60 * 60 * 24));
    const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((distance % (1000 * 60)) / 1000);

    if (this.daysEl) this.daysEl.textContent = this.pad(days);
    if (this.hoursEl) this.hoursEl.textContent = this.pad(hours);
    if (this.minutesEl) this.minutesEl.textContent = this.pad(minutes);
    if (this.secondsEl) this.secondsEl.textContent = this.pad(seconds);
  }

  pad(num) {
    return String(num).padStart(2, '0');
  }

  showExpired() {
    if (this.daysEl) this.daysEl.textContent = '00';
    if (this.hoursEl) this.hoursEl.textContent = '00';
    if (this.minutesEl) this.minutesEl.textContent = '00';
    if (this.secondsEl) this.secondsEl.textContent = '00';
  }

  stop() {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
  }
}

// ============================================================================
// INITIALIZE
// ============================================================================
const arGridV3 = new ARGridV3();

// Export for potential external use
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { ARGridV3, ARSlider, ARCountdown };
}
